import { useEffect, useRef, useState } from 'react';
import Dialog from '#lib/components/dialog.jsx';
import { useTemplates } from '#@itinerary/planner/use-templates.js';
import Button from '#lib/components/button.jsx';
import { addClasses, cn } from '#lib/helpers/class.js';
import { Icon } from '@iconify-icon/react/dist/iconify.mjs';
import { HotelModal, PlanDetail } from '#@itinerary/planner/detail.jsx';
import { arrayMove } from '#lib/utils/array.js';
import Input from '#lib/components/input.jsx';
import { newId } from '#helpers/helpers.js';
import { PlanRow } from '#@itinerary/planner/days-overview.jsx';
import { AddRowButton } from '#@itinerary/planner/row.jsx';
import { PlanTemplate } from '#routes/manage/templates/template-details.jsx';
import { filterTemplates } from '#routes/manage/templates/helpers.js';
import { LoadingIndicator, LoadingSkeleton } from '#lib/components/loading.jsx';
import Checkbox from '#lib/components/checkbox.jsx';
import toast from 'react-hot-toast';

export function TemplatesModal(props) {
    const { isOpen, onClose, type, plan, planId, onUseTemplate, mode, setMode, addPlan, multiselection, lang } = props;

    const { templates, addTemplate } = useTemplates(type === 'template' ? '' : type);
    const [selectedTemplateIndex] = useState(0);
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [postCreateMode, setPostCreateMode] = useState(null);
    const [editableTemplate, setEditableTemplate] = useState(Object.assign({}, { ...plan, title: plan?.name }));
    const [templateError, setTemplateError] = useState({});

    useEffect(() => {
        const clonedPlan = Object.assign({}, { ...plan, title: plan?.name });
        setEditableTemplate(clonedPlan);
    }, [mode, selectedTemplateIndex]);

    const handleChange = (event, id) => {
        const { name, value } = event.target;

        //ROOMS
        if (id) {
            const state = structuredClone(editableTemplate);
            const room = state.rooms.find((room) => room.id === id);
            room[name] = value;
            setEditableTemplate(state);
            return;
        }
        //DATI NORMALI
        setEditableTemplate({ ...editableTemplate, [name]: value });
        return;
    };

    const handleMoveGallery = (at, from, to) => {
        const [_, __, roomId] = at;
        const updated = structuredClone(editableTemplate);
        if (roomId) {
            const room = updated.rooms.find((room) => room.id === roomId);
            room.gallery = arrayMove(room.gallery, from, to);
        } else updated.gallery = arrayMove(updated.gallery, from, to);
        setEditableTemplate(updated);
    };

    const handleRemoveRoom = (_, __, id) => {
        const confirm = window.confirm(_t.global.confirm());
        if (!confirm) return;
        if (selectedRoomId === id) setSelectedRoomId(null);
        const index = editableTemplate.rooms.findIndex((room) => room.id === id);
        editableTemplate.rooms.splice(index, 1);
    };

    const handleSave = async () => {
        const error = {
            title: !editableTemplate.title,
            name: !editableTemplate.name,
            type: !editableTemplate.type
        };
        setTemplateError(error);
        if (error.title || error.name || error.type) {
            // toast.error(_t.global.validationError());
            return;
        }
        const addResponse = await addTemplate.mutateAsync(editableTemplate, templates);
        if (!addResponse.ok) return;
        if (mode === 'upload') onUseTemplate(editableTemplate, planId);
        if (postCreateMode === 'upload') {
            setPostCreateMode(null);
            setMode('upload');
        } else {
            setMode(null);
            onClose();
        }
    };

    return (
        <Dialog isOpen={isOpen} onClose={() => ''}>
            <Dialog.Panel
                static
                className={cn(
                    `flex flex-col space-y-6 min-w-[300px] w-full max-w-[300px]`,
                    mode === 'create' && 'max-w-[600px]',
                    mode === 'uploadTemplate' && 'max-w-[900px]'
                )}
            >
                <div className="pr-4">
                    <Dialog.CloseButton
                        onClick={() => {
                            if (type === 'template') onClose();
                            if (mode) {
                                setMode(null);
                                setEditableTemplate(plan);
                            } else onClose();
                        }}
                    />
                    <b>{_t.edit.templatesText()}</b>
                    <p className="pr-2 mt-1 text-sm">{_t.edit.templatesSubText()}</p>
                </div>
                {mode === null && (
                    <div className="grid w-full grid-cols-2 gap-2 place-items-center">
                        <div
                            className={cn(
                                'flex flex-col gap-2 items-center w-28 px-2 py-4 bg-slate-100 border border-slate-200 rounded-lg cursor-pointer hover:bg-primary-50 hover:border-primary-400'
                            )}
                            onClick={() => setMode('create')}
                        >
                            <Icon icon="ic:round-plus" width="1.8rem" height="1.8rem" />
                            {_t.global.create()}
                        </div>
                        <div
                            className={cn(
                                'flex flex-col gap-2 items-center w-28 px-2 py-4 bg-slate-100 border border-slate-200 rounded-lg cursor-pointer hover:bg-primary-50 hover:border-primary-400'
                            )}
                            onClick={() => setMode('upload')}
                        >
                            <Icon icon="material-symbols:download" width="1.8rem" height="1.8rem" />
                            {_t.global.load()}
                        </div>
                    </div>
                )}

                {mode === 'upload' && (
                    <div className="grid w-full grid-cols-2 gap-2 place-items-center">
                        <div
                            className={cn(
                                'flex flex-col gap-2 items-center w-28 px-2 py-4 bg-slate-100 border border-slate-200 rounded-lg cursor-pointer hover:bg-primary-50 hover:border-primary-400'
                            )}
                            onClick={() => setMode('uploadTemplate')}
                        >
                            <Icon icon="majesticons:book-plus-line" width="1.8rem" height="1.8rem" />
                            {_t.edit.planner.template()}
                        </div>
                        <div
                            className={cn(
                                'flex flex-col gap-2 items-center w-28 px-2 py-4 bg-slate-100 border border-slate-200 rounded-lg cursor-pointer hover:bg-primary-50 hover:border-primary-400'
                            )}
                            onClick={() => setMode('uploadHotel')}
                        >
                            <Icon icon="carbon:hotel" width="1.8rem" height="1.8rem" />
                            {_t.edit.planner.hotelbeds()}
                        </div>
                    </div>
                )}
                {mode === 'uploadTemplate' && (
                    <UploadTemplatePanel
                        planId={planId}
                        planName={plan?.name ?? 'new plan'}
                        type={type !== 'template' ? type : ''}
                        onUseTemplate={onUseTemplate}
                        onClose={() => {
                            setMode(null);
                            onClose();
                        }}
                        handleCreateMode={() => {
                            setMode('create');
                            setPostCreateMode('upload');
                        }}
                        addPlan={addPlan}
                        multiselection={multiselection}
                    />
                )}
                {mode === 'uploadHotel' && (
                    <HotelModal
                        isOpen={true}
                        onClose={() => setMode('upload')}
                        language={lang}
                        fillPlan={(data) => {
                            let id = planId;
                            if (!id) id = addPlan('accommodation', '');
                            onUseTemplate(data, id);
                            onClose();
                        }}
                    />
                )}
                {mode === 'create' && (
                    <>
                        <div className="overflow-auto max-h-[60vh] border border-neutral-100 rounded-lg">
                            <PlanDetail
                                {...editableTemplate}
                                onEdit={() => ''}
                                onChange={handleChange}
                                onAdd={() => {
                                    const newRooms = structuredClone(editableTemplate.rooms || []);
                                    newRooms.push({
                                        name: '',
                                        description: '',
                                        pax: 1,
                                        gallery: [],
                                        id: newId()
                                    });
                                    setEditableTemplate({ ...editableTemplate, rooms: newRooms });
                                }}
                                onDelete={handleRemoveRoom}
                                setSelectedRoomId={setSelectedRoomId}
                                selectedRoomId={selectedRoomId}
                                onMoveGallery={handleMoveGallery}
                                isTemplate={true}
                                templateError={templateError}
                            />
                        </div>
                        <div className="flex justify-end w-full mt-4">
                            <Button look="primary" onClick={handleSave} busy={addTemplate.isLoading}>
                                {_t.global.save()}
                            </Button>
                        </div>
                    </>
                )}
            </Dialog.Panel>
        </Dialog>
    );
}

function UploadTemplatePanel(props) {
    const { planId, planName, onUseTemplate, handleCreateMode, onClose, type, addPlan, multiselection } = props;
    const { templates, destroyTemplate, isLoading } = useTemplates(type);

    const [searchValue, setSearchValue] = useState('');
    const [usableTemplates, setUsableTemplates] = useState(filterTemplates(templates));
    const [selectedTemplatesIds, setSelectedTemplatesIds] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const selectAllRef = useRef(null);
    const filteredTemplates = filterTemplates(usableTemplates, searchValue);
    const template = filteredTemplates?.find((t) => t.id === selectedTemplateId);
    useEffect(() => {
        setUsableTemplates(templates);
    }, [templates]);

    const handleDelete = (template, index) => {
        const confirm = window.confirm(_t.edit.confirmDeleteTemplate());
        if (!confirm) return;

        destroyTemplate.mutate({ type: template.type, id: template.id });
        setSelectedTemplatesIds((prev) => {
            const index = prev.indexOf(template.id);
            if (index > -1) {
                const newSelectedTemplatesIndexes = [...prev];
                newSelectedTemplatesIndexes.splice(index, 1);
                return newSelectedTemplatesIndexes;
            }
        }); /* 
        if (index === selectedTemplateIndex && index === usableTemplates.length - 1 && index !== 0)
            setSelectedTemplateIndex((prev) => prev - 1); */
    };

    const handleApplyTemplate = () => {
        console.log(multiselection);
        if (multiselection ? !selectedTemplatesIds.length : !selectedTemplateId)
            return toast.error(_t.edit.planner.selectTemplate());
        const confirm = window.confirm(`Apply selected templates on '${planName}'`);
        if (!confirm) return;
        if (!multiselection) {
            const template = templates?.find((t) => t.id === selectedTemplateId);
            if (!type) {
                const id = addPlan(template.type, '');
                onUseTemplate(template, id);
                onClose();
                return;
            }
            onUseTemplate(template, planId);
            onClose();
            return;
        }
        for (const id of selectedTemplatesIds) {
            const template = templates.find((t) => t.id === id);
            if (!type) {
                const pid = addPlan(template.type, '');
                onUseTemplate(template, pid);
                continue;
            }
            onUseTemplate(template, planId);
        }
        onClose();
    };

    /* useEffect(() => {
        if (!selectAllRef.current) return;
        // se almeno una è selezionata (ma non tutte)
        if (selectedTemplatesIds.length > 0 && selectedTemplatesIds.length < filteredTemplates.length) {
            selectAllRef.current.indeterminate = true;
            return;
        }
        // se nessuna è selezionata
        selectAllRef.current.indeterminate = false;
        return;
    }, [selectedTemplatesIds.length, filteredTemplates?.length]); */

    if (isLoading)
        return (
            <LoadingSkeleton className="flex items-center justify-center h-[60vh]">
                <LoadingIndicator />
            </LoadingSkeleton>
        );
    return (
        <>
            <div className="w-full h-[70vh] mb-4 grid grid-cols-1 md:grid-cols-2 md:gap-2 border border-neutral-200 rounded overflow-hidden">
                <div className="flex flex-col flex-1 w-full gap-2 p-2 pb-3 overflow-y-auto md:max-h-full bg-neutral-100">
                    <Input.Full
                        value={searchValue}
                        placeholder={_t.edit.templatesSearchPlaceholder()}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                    {multiselection && (
                        <div className="flex items-center gap-2 pl-2">
                            <input
                                type="checkbox"
                                ref={selectAllRef}
                                checked={filteredTemplates?.every((el) => selectedTemplatesIds.includes(el.id))}
                                label={_t.edit.selectAll()}
                                onChange={(e) => {
                                    switch (e.target.checked) {
                                        case true:
                                            setSelectedTemplatesIds((prev) => [
                                                ...prev,
                                                ...filteredTemplates.map((t) => t.id)
                                            ]);
                                            break;
                                        case false:
                                            for (const template of filteredTemplates) {
                                                setSelectedTemplatesIds((prev) =>
                                                    prev.filter((el) => el !== template.id)
                                                );
                                            }
                                            break;
                                    }
                                }}
                                className="h-4 w-4 translate-y-0.5 cursor-pointer rounded border-neutral-300"
                            />
                            <span className="text-sm text-neutral-700">{_t.global.selectAll()}</span>
                        </div>
                    )}
                    <ul className="flex flex-col w-full mb-2 overflow-auto border divide-y rounded divide-neutral-200 border-neutral-200">
                        {filteredTemplates?.map((t, idx) => {
                            const selected = t.id === selectedTemplateId;
                            return (
                                <div className="flex items-center gap-2" key={t.id}>
                                    {multiselection && (
                                        <Checkbox.Full
                                            key={newId()}
                                            checked={selectedTemplatesIds.includes(t.id)}
                                            onChange={() => {
                                                setSelectedTemplatesIds((prev) =>
                                                    prev.includes(t.id)
                                                        ? prev.filter((i) => i !== t.id)
                                                        : [...prev, t.id]
                                                );
                                            }}
                                            className="pl-2"
                                        />
                                    )}
                                    <PlanRow
                                        key={t.id}
                                        selfId={t.id}
                                        selected={selected}
                                        onSelect={() => {
                                            console.log(t.id);
                                            setSelectedTemplateId(t.id);
                                        }}
                                        onDelete={() => handleDelete(t, idx)}
                                        style={{ zIndex: 0, width: '100%' }}
                                        {...t}
                                        name={t?.title}
                                    />
                                </div>
                            );
                        })}
                    </ul>

                    <AddRowButton onClick={() => handleCreateMode()}>{_t.global.new()}</AddRowButton>
                </div>
                <div className="w-full pb-4 overflow-auto border-t md:pt-0 md:border-t-0 border-neutral-300">
                    {template ? (
                        <PlanTemplate key={template?.id} {...template} />
                    ) : (
                        <div className="flex items-center justify-center w-full h-full bg-neutral-50">
                            <p className="w-2/3 py-16 italic text-center text-neutral-600">{_t.edit.detailText()}</p>
                        </div>
                    )}
                </div>
            </div>
            <div className="flex justify-end w-full gap-4">
                <Button look="primary" onClick={handleApplyTemplate}>
                    {_t.edit.useTemplate()}
                </Button>
            </div>
        </>
    );
}
